// ─────────────────────────────────────────────────────────────────────────────
// 🔑 CLOSING MODULE STEP 5: APPROVE AND OVERRIDE WAIT FOR THE FINAL 1003 CHECK (push B, Jesse 2026-10-31).
//
// 🚨 WHY: the module stored no Final 1003 result until the card's GET final-1003-check answered, and "no result" read
// as "not bad" — so for that moment "Approve Final Underwriting" and "Override & Approve Anyway" were live. A fast
// click got the server's 409 FINAL_1003_MISMATCH (the server gate held) and an uncaught error; on GitHub's slower runner
// the closing-final-1003 E2E clicked straight through it (.scratch/reports/races-1031.md §1).
//
// So both are off, labelled "Checking Final 1003…", until the check responds, and come back only if it passes.
// 🚨 "CHECK NOT AVAILABLE" IS NOT A PASS (push C item 3, Jesse 2026-10-31 — backend utils/final1003Approve, the server's
// own reading in routes/closingModule 5:approve): when the check is unavailable (available: false — no 1003 versions
// table yet) or failed (a read that errored — the card reports { ok: false, failed: true }), Approve stays off, labelled
// "Check unavailable, retry", with a Retry beside it; "Override & Approve Anyway" is shown and allowed only with a
// written reason (recorded on the file with the staff id and the time). A mismatch keeps both off, as before. The
// override keeps its other rules: it shows while required documents are stale or missing, and skips only that check.
// ─────────────────────────────────────────────────────────────────────────────

export const CHECKING_LABEL = 'Checking Final 1003…';
export const UNAVAILABLE_LABEL = 'Check unavailable, retry';
export const OVERRIDE_REASON_MIN = 10;   // backend utils/final1003Approve OVERRIDE_REASON_MIN

/** The check could not be run: not available on this file, or the read failed. Never a pass. */
export const isUnavailable = (f) => !!f && (f.available === false || f.failed === true);

/**
 * @param {object|null} final1003   the card's last result (null until the check responds)
 * @param {object|null} finalUwCheck the document refresh check (null until read)
 * @param {string} [overrideReason] the reason typed for the override (required while the check is unavailable)
 * @returns {{ pending, bad, unavailable, approve: { label, disabled }, retry: { show }, override: { show, label, disabled, needsReason } }}
 */
export function step5Buttons(final1003, finalUwCheck, overrideReason = '') {
  const pending = final1003 == null;
  const unavailable = !pending && isUnavailable(final1003);
  const bad = !pending && !unavailable && !final1003.ok;
  const docsStale = !!(finalUwCheck && !finalUwCheck.all_required_current);
  const reasonOk = String(overrideReason || '').trim().length >= OVERRIDE_REASON_MIN;
  return {
    pending,
    bad,
    unavailable,
    approve: { label: pending ? CHECKING_LABEL : unavailable ? UNAVAILABLE_LABEL : 'Approve Final Underwriting', disabled: pending || bad || unavailable || docsStale },
    retry: { show: unavailable },
    override: { show: docsStale || unavailable, label: pending ? CHECKING_LABEL : 'Override & Approve Anyway', needsReason: unavailable,
      disabled: pending || bad || (unavailable && !reasonOk) },
  };
}

/**
 * 🚨 CLEAR FOR CLOSING READS THE SAME RULE (D2 A, Jesse 2026-11-01 — backend utils/final1003Approve ctcGate). Pending or
 * a mismatch → off. Unavailable → on only with a written reason (sent as final1003_override + override_reason, recorded
 * with the staff id and the time). Passing → on.
 * @returns {{ disabled: boolean, needsReason: boolean, label: string }}
 */
export function ctcButton(final1003, overrideReason = '', closingDate = '') {
  const pending = final1003 == null;
  const unavailable = !pending && isUnavailable(final1003);
  const bad = !pending && !unavailable && !final1003.ok;
  const reasonOk = String(overrideReason || '').trim().length >= OVERRIDE_REASON_MIN;
  return {
    needsReason: unavailable,
    label: pending ? CHECKING_LABEL : unavailable ? 'Override & Clear for Closing' : 'Clear for Closing',
    disabled: !closingDate || pending || bad || (unavailable && !reasonOk),
  };
}
